import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos, videoCategories, latestVideos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';
import type { VideoItem } from '@/mocks/videos';
import type { ArticleItem } from '@/mocks/articles';
import VideoCard from '@/components/base/VideoCard';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const video = useMemo(() => videos.find(v => v.slug === slug), [slug]);
  const article = useMemo(() => articles.find(a => a.slug === slug), [slug]);
  const content: VideoItem | ArticleItem | undefined = video || article;

  if (!content) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
          <i className="ri-error-warning-line text-3xl w-10 h-10 flex items-center justify-center" />
        </div>
        <h1 className="text-xl font-bold text-foreground-900 mb-2">内容未找到</h1>
        <p className="text-foreground-500 mb-6">您访问的内容可能已被移除或不存在</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-home-line w-4 h-4 flex items-center justify-center" />
          返回首页
        </Link>
      </div>
    );
  }

  const isVideo = 'duration' in content;
  const categoryLabel = content.category || '';
  const relatedVideos = latestVideos.filter(v => v.slug !== slug).slice(0, 6);

  // Article schema
  const articleSchema = isVideo ? {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: content.title,
    description: content.description || '',
    image: (content as VideoItem).thumbnail,
    dateCreated: (content as VideoItem).updateTime,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: (content as VideoItem).rating,
      bestRating: '10',
      ratingCount: (content as VideoItem).views,
    },
  } : {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: (content as ArticleItem).title,
    description: (content as ArticleItem).excerpt,
    image: (content as ArticleItem).coverImage,
    datePublished: (content as ArticleItem).publishDate,
    author: {
      '@type': 'Person',
      name: (content as ArticleItem).author,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <Breadcrumb
        items={[
          { label: '首页', path: '/' },
          { label: categoryLabel, path: isVideo ? `/category/${(content as VideoItem).categorySlug}` : undefined },
          { label: content.title },
        ]}
      />

      <article className="max-w-4xl mx-auto px-4 md:px-6 pb-10">
        {/* Cover Image */}
        {isVideo ? (
          <div className="relative w-full aspect-video rounded-xl overflow-hidden mb-6 mt-4">
            <img
              src={(content as VideoItem).thumbnail}
              alt={content.title}
              title={`${content.title} - 密桃视频`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 flex items-center gap-2">
              <span className="bg-accent-500 text-white text-xs px-2.5 py-1 rounded-full whitespace-nowrap">
                {(content as VideoItem).source}
              </span>
              <span className="bg-black/60 text-white text-xs px-2.5 py-1 rounded-full whitespace-nowrap">
                时长 {(content as VideoItem).duration}
              </span>
            </div>
          </div>
        ) : (
          <div className="relative w-full aspect-[2/1] rounded-xl overflow-hidden mb-6 mt-4">
            <img
              src={(content as ArticleItem).coverImage}
              alt={content.title}
              title={content.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Title & Meta */}
        <header>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium whitespace-nowrap">
              {categoryLabel}
            </span>
            {!isVideo && (
              <span className="text-xs text-foreground-400">
                {(content as ArticleItem).author} · {(content as ArticleItem).publishDate}
              </span>
            )}
          </div>
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold font-heading text-foreground-900 leading-tight">
            {content.title}
          </h1>

          {isVideo && (
            <div className="flex items-center gap-4 mt-3 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line w-4 h-4 flex items-center justify-center" />
                {(content as VideoItem).views >= 10000
                  ? ((content as VideoItem).views / 10000).toFixed(1) + '万次播放'
                  : (content as VideoItem).views.toLocaleString() + '次播放'}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500 w-4 h-4 flex items-center justify-center" />
                评分 {(content as VideoItem).rating}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center" />
                更新于 {(content as VideoItem).updateTime}
              </span>
            </div>
          )}
        </header>

        {/* Content */}
        <div className="mt-8">
          {isVideo ? (
            <>
              <div className="prose prose-sm max-w-none text-foreground-700">
                <p className="text-base leading-relaxed">
                  {(content as VideoItem).description}
                </p>
              </div>
              <div className="flex items-center gap-2 mt-6 flex-wrap">
                {(content as VideoItem).tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </>
          ) : (
            <div
              className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: (content as ArticleItem).content }}
            />
          )}
        </div>

        {/* Related Videos */}
        <section className="mt-12 pt-8 border-t border-background-200/70">
          <h3 className="text-lg font-bold font-heading text-foreground-900 mb-5">
            相关推荐
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {relatedVideos.map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
        </section>
      </article>
    </>
  );
}